import { associateEventsSelectors, AssociateSetupContext, AuthenticationContext, chatActions, chatSelectors, ChatType, useAppDispatch, useAppSelector, useGetActiveConversationsUnreadMessagesCount, useGetActiveHeroConversations, useUpdateAssociate } from '@vs-online-only-app/state';
import { FC, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

type Props = {
  children: ReactNode;
};

interface WindowMessageEvent extends MessageEvent {
  data: {
    eventName: string;
    payload?: any;
  };
}

const isStandalone = window === window.top;

const BrowserNotifications: FC<Props> = ({ children }) => {
  const dispatch = useAppDispatch();
  const { user } = useContext(AuthenticationContext);
  const updateAssociate = useUpdateAssociate();
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const currentIncomingChatRequestEvent = useAppSelector(
    associateEventsSelectors.selectCurrentIncomingConversationRequestEvent,
  );
  const { associateSetupState } = useContext(AssociateSetupContext);

  const location = useLocation();
  const isChatPage = location.pathname.startsWith('/chats');
  const navigate = useNavigate();

  const selectedConversation = useAppSelector(
      chatSelectors.selectSelectedConversation
    );
  const { data: activeConversations = [] } = useGetActiveHeroConversations();
  const { data: { unreadMessagesCountPerConversation } } = useGetActiveConversationsUnreadMessagesCount();
  const openNewMessageNotifications = useRef<{ [key: string]: boolean }>({});

  const [documentState, setDocumentState] = useState(document.visibilityState);
  const documentIsCurrentlyVisible = documentState === 'visible';

  useEffect(() => {
    const handleVisibilityChange = () => {
      setDocumentState(document.visibilityState);
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (isStandalone || !notificationsEnabled) return;

    const hideNotification = () => {
      window.top?.postMessage({
        eventName: 'hideNativeNotification',
        payload: {
          id: 'newConversation',
        },
      }, '*');
    };

    if (!currentIncomingChatRequestEvent) {
      // Hide any existing browser notification when there is no incoming chat request anymore
      hideNotification();
      return;
    } else if (documentIsCurrentlyVisible) {
      // Don't try to show a browser notification if the document is currently visible
      // User doesn't need to see both incoming conversation card and browser notification
      return;
    }

    const retailerName = associateSetupState?.retailer?.name;
    const shopperName = currentIncomingChatRequestEvent?.conversation?.conversationStarter?.firstName;
    window.top?.postMessage({
      eventName: 'showNativeNotification',
      payload: {
        id: 'newConversation',
        title: 'New incoming chat',
        body: `${retailerName ? `${retailerName}: ` : ''}There is a new incoming chat${shopperName ? ` from ${shopperName}` : ''}, claim it!`,
        data: {
          associateEventId: currentIncomingChatRequestEvent.id,
        },
      },
    }, '*');

    return () => {
      hideNotification();
    };
  }, [currentIncomingChatRequestEvent, associateSetupState?.retailer?.name, documentIsCurrentlyVisible, notificationsEnabled]);

  useEffect(() => {
    if (isStandalone || !notificationsEnabled) return;

    // Figure out which conversations have unread messages that we should notify the user about. But becouse the value
    // of "selectedConversation" is kept when user navigates to other places in the app we need extra check for selected
    // conversation to determine which page user is on. We also would like to always show notifications when the brower
    // tab is not currently visible
    const conversationsWithUnreadMessages = activeConversations.filter((conversation) => {
      return unreadMessagesCountPerConversation[conversation.id] > 0 && (conversation.id !== selectedConversation?.id || !isChatPage || !documentIsCurrentlyVisible);
    });

    for (const conversation of conversationsWithUnreadMessages) {
      if (openNewMessageNotifications.current[conversation.id]) continue;
      openNewMessageNotifications.current[conversation.id] = true;
      const shopperName = conversation?.conversationStarter?.firstName;
      const retailerName = associateSetupState?.retailer?.name;
      window.top?.postMessage({
        eventName: 'showNativeNotification',
        payload: {
          id: `newMessage-${conversation.id}`,
          title: 'New chat message',
          body: `${retailerName ? `${retailerName}: ` : ''}Reply from ${shopperName ? `${shopperName}` : 'shopper'}`,
          data: {
            conversationId: conversation.id,
          },
        },
      }, '*');
    }

    if (documentIsCurrentlyVisible) {
      const notificationsToClose = activeConversations.filter((conversation) => {
        return unreadMessagesCountPerConversation[conversation.id] === 0 && openNewMessageNotifications.current[conversation.id];
      });

      for (const conversation of notificationsToClose) {
        openNewMessageNotifications.current[conversation.id] = false;
        window.top?.postMessage({
          eventName: 'hideNativeNotification',
          payload: {
            id: `newMessage-${conversation.id}`,
          },
        }, '*');
      }
    }
  }, [activeConversations, unreadMessagesCountPerConversation, selectedConversation, associateSetupState?.retailer?.name, isChatPage, documentIsCurrentlyVisible, notificationsEnabled]);

  useEffect(() => {
    if (isStandalone) return;
    const parentWindowListener = (event: WindowMessageEvent) => {
      if (!event.data?.eventName) return;
      const { eventName, payload } = event.data;
      if (eventName === 'activateNotificationsResult') {
        const allowShow = payload.permission === 'granted';
        setNotificationsEnabled(allowShow);
        if (payload.userAction) {
          updateAssociate.mutate({
            browserNotifications: {
              allowShow,
            },
          });
        }
      } else if (eventName === 'nativeNotificationClicked') {
        if (payload.id?.startsWith('newMessage-')) {
          const conversationId = payload.customData?.conversationId;
          const conversation = activeConversations.find((conversation) => conversation.id === conversationId);
          if (!conversation) return;
          navigate(`/chats?id=${conversationId}`);
          dispatch(chatActions.setSelectedConversation(conversation));
          dispatch(chatActions.setSelectedChatType(ChatType.Active));
        }
      }
    };
    window.addEventListener('message', parentWindowListener);
    return () => {
      window.removeEventListener('message', parentWindowListener);
    };
  }, [activeConversations, dispatch, navigate, updateAssociate]);

  useEffect(() => {
    if (isStandalone) return;
    if (user?.available && user?.features?.browserNotifications?.allowShow !== false) {
      // Unless user has explicitly declined browser notifications, let's try and activate them, if already activated
      // this is a no-op, but setting can be enabled but browser notifications have not yet been granted in the browser
      window.top?.postMessage({
        eventName: 'activateNotifications',
        payload: {
          dialog: {
            title: 'Enable Browser Notifications',
            message: 'Stay updated with real-time alerts for new incoming chats and messages, even when you’re not actively using this window or tab.\n\nWould you like to turn on browser notifications?',
            acceptText: 'Yes',
            declineText: 'No',
          },
        }
      }, '*');
    }
  }, [user?.available, user?.features?.browserNotifications?.allowShow]);

  return children;
};

export default BrowserNotifications;
